import React, { useState, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  Search, 
  X, 
  ArrowRight, 
  Clock, 
  Settings, 
  BarChart3, 
  Brain,
  Ticket,
  User,
  Zap,
  Loader2
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import SearchAPI from '../../services/search'
import { debounce } from '../../utils/searchUtils'

const GlobalSearchModal = ({ isOpen, onClose, initialQuery = '' }) => {
  const navigate = useNavigate()
  const [query, setQuery] = useState(initialQuery)
  const [results, setResults] = useState(null)
  const [loading, setLoading] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const [recentSearches, setRecentSearches] = useState([])

  // Load recent searches from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('triage_recent_searches')
    if (saved) {
      try {
        setRecentSearches(JSON.parse(saved))
      } catch (error) {
        console.error('Failed to load recent searches:', error)
      }
    }
  }, [])

  // Set initial query when modal opens
  useEffect(() => {
    if (isOpen && initialQuery) {
      setQuery(initialQuery)
      performSearch(initialQuery)
    }
  }, [isOpen, initialQuery])

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce((searchQuery) => {
      if (searchQuery.trim()) {
        performSearch(searchQuery)
      } else {
        setResults(null)
        setLoading(false)
      }
    }, 300),
    []
  )

  // Perform search
  const performSearch = async (searchQuery) => {
    if (!searchQuery.trim()) {
      setResults(null)
      return
    }

    setLoading(true)
    try {
      const searchResults = await SearchAPI.globalSearch(searchQuery)
      setResults(searchResults)
      setSelectedIndex(-1)
    } catch (error) {
      console.error('Search error:', error)
      setResults({
        tickets: [],
        activities: [],
        settings: [],
        suggestions: [],
        total: 0
      })
    } finally {
      setLoading(false)
    }
  }

  // Handle input change
  const handleInputChange = (e) => {
    const value = e.target.value
    setQuery(value)
    setLoading(!!value.trim())
    debouncedSearch(value)
  }

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!results || results.total === 0) return

    const allResults = [
      ...results.tickets.map(item => ({ ...item, type: 'ticket' })),
      ...results.activities.map(item => ({ ...item, type: 'activity' })),
      ...results.settings.map(item => ({ ...item, type: 'setting' }))
    ]

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setSelectedIndex(prev => 
          prev < allResults.length - 1 ? prev + 1 : 0
        )
        break
      case 'ArrowUp':
        e.preventDefault()
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : allResults.length - 1
        )
        break
      case 'Enter':
        e.preventDefault()
        if (selectedIndex >= 0) {
          handleResultClick(allResults[selectedIndex])
        }
        break
      case 'Escape':
        onClose()
        break
    }
  }

  // Handle result click
  const handleResultClick = (result) => {
    // Save to recent searches
    const newRecent = [
      query,
      ...recentSearches.filter(item => item !== query)
    ].slice(0, 5)
    setRecentSearches(newRecent)
    localStorage.setItem('triage_recent_searches', JSON.stringify(newRecent))

    // Navigate based on result type
    switch (result.type) {
      case 'ticket':
        navigate(`/app/tickets/${result.id}`)
        break
      case 'activity':
        navigate('/app/dashboard', { state: { searchTerm: query } })
        break
      case 'setting':
        navigate('/app/settings', { state: { activeTab: result.section } })
        break
      default:
        navigate(result.url || '/app/dashboard')
    }
    onClose()
  }

  // Handle recent search click
  const handleRecentSearchClick = (searchTerm) => {
    setQuery(searchTerm)
    performSearch(searchTerm)
  }

  // Clear search
  const clearSearch = () => {
    setQuery('')
    setResults(null)
    setSelectedIndex(-1)
    setLoading(false)
  }

  const getResultIcon = (type) => {
    switch (type) {
      case 'ticket':
        return <Ticket className="w-4 h-4" />
      case 'activity':
        return <Brain className="w-4 h-4" />
      case 'setting':
        return <Settings className="w-4 h-4" />
      default:
        return <Search className="w-4 h-4" />
    }
  }

  const getResultTypeLabel = (type) => {
    switch (type) {
      case 'ticket':
        return 'Ticket'
      case 'activity':
        return 'Activity'
      case 'setting':
        return 'Setting'
      default:
        return 'Result'
    }
  }

  if (!isOpen) return null

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center pt-20"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: -20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: -20 }}
          className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 max-h-[70vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center p-4 border-b border-secondary-200">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-secondary-400" />
              <input
                type="text"
                value={query}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder="Search tickets, activities, settings..."
                className="w-full pl-10 pr-12 py-3 border border-secondary-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                autoFocus
              />
              {loading && (
                <Loader2 className="absolute right-10 top-1/2 transform -translate-y-1/2 w-4 h-4 text-primary-600 animate-spin" />
              )}
              {query && (
                <button
                  onClick={clearSearch}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 text-secondary-400 hover:text-secondary-600 rounded"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <button
              onClick={onClose}
              className="ml-3 p-2 text-secondary-400 hover:text-secondary-600 rounded-lg hover:bg-secondary-100 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {!query ? (
              /* Recent Searches & Quick Actions */
              <div className="p-6">
                {recentSearches.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-sm font-medium text-secondary-900 mb-3 flex items-center">
                      <Clock className="w-4 h-4 mr-2" />
                      Recent Searches
                    </h3>
                    <div className="space-y-2">
                      {recentSearches.map((search, index) => (
                        <button
                          key={index}
                          onClick={() => handleRecentSearchClick(search)}
                          className="w-full text-left px-3 py-2 text-sm text-secondary-700 hover:bg-secondary-50 rounded-lg transition-colors"
                        >
                          <div className="flex items-center justify-between">
                            <span>{search}</span>
                            <ArrowRight className="w-3 h-3 text-secondary-400" />
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Quick Actions */}
                <div>
                  <h3 className="text-sm font-medium text-secondary-900 mb-3 flex items-center">
                    <Zap className="w-4 h-4 mr-2" />
                    Quick Actions
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => navigate('/app/dashboard')}
                      className="flex items-center p-3 text-sm text-secondary-700 hover:bg-secondary-50 rounded-lg transition-colors"
                    >
                      <BarChart3 className="w-4 h-4 mr-3 text-primary-600" />
                      <span>Dashboard</span>
                    </button>
                    <button
                      onClick={() => navigate('/app/analytics')}
                      className="flex items-center p-3 text-sm text-secondary-700 hover:bg-secondary-50 rounded-lg transition-colors"
                    >
                      <Brain className="w-4 h-4 mr-3 text-green-600" />
                      <span>Analytics</span>
                    </button>
                    <button
                      onClick={() => navigate('/app/settings')}
                      className="flex items-center p-3 text-sm text-secondary-700 hover:bg-secondary-50 rounded-lg transition-colors"
                    >
                      <Settings className="w-4 h-4 mr-3 text-blue-600" />
                      <span>Settings</span>
                    </button>
                    <button
                      onClick={() => navigate('/app/integrations')}
                      className="flex items-center p-3 text-sm text-secondary-700 hover:bg-secondary-50 rounded-lg transition-colors"
                    >
                      <Zap className="w-4 h-4 mr-3 text-purple-600" />
                      <span>Integrations</span>
                    </button>
                  </div>
                </div>
              </div>
            ) : loading ? (
              /* Loading State */
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
                <span className="ml-3 text-secondary-600">Searching...</span>
              </div>
            ) : results && results.total > 0 ? (
              /* Search Results */
              <div className="p-4">
                <div className="text-sm text-secondary-500 mb-4">
                  Found {results.total} results for "{query}"
                </div>
                
                <div className="space-y-1">
                  {[
                    ...results.tickets.map(item => ({ ...item, type: 'ticket' })),
                    ...results.activities.map(item => ({ ...item, type: 'activity' })),
                    ...results.settings.map(item => ({ ...item, type: 'setting' }))
                  ].map((result, index) => (
                    <button
                      key={`${result.type}-${result.id}`}
                      onClick={() => handleResultClick(result)}
                      className={`w-full text-left p-3 rounded-lg transition-colors ${
                        index === selectedIndex 
                          ? 'bg-primary-50 border border-primary-200' 
                          : 'hover:bg-secondary-50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="text-secondary-500">
                            {getResultIcon(result.type)}
                          </div>
                          <div>
                            <div className="font-medium text-secondary-900">
                              {result.title}
                            </div>
                            <div className="text-sm text-secondary-600">
                              {result.description}
                            </div>
                            <div className="text-xs text-secondary-500 mt-1">
                              {getResultTypeLabel(result.type)}
                              {result.timestamp && (
                                <span className="ml-2">
                                  • {new Date(result.timestamp).toLocaleDateString()}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <ArrowRight className="w-4 h-4 text-secondary-400" />
                      </div>
                    </button>
                  ))}
                </div>

                {/* Search Suggestions */}
                {results.suggestions.length > 0 && (
                  <div className="mt-6 pt-4 border-t border-secondary-200">
                    <h4 className="text-sm font-medium text-secondary-900 mb-2">
                      Try searching for:
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {results.suggestions.map((suggestion, index) => (
                        <button
                          key={index}
                          onClick={() => handleRecentSearchClick(suggestion)}
                          className="px-3 py-1 text-sm text-primary-700 bg-primary-50 rounded-full hover:bg-primary-100 transition-colors"
                        >
                          {suggestion}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : query ? (
              /* No Results */
              <div className="flex flex-col items-center justify-center py-12">
                <Search className="w-12 h-12 text-secondary-300 mb-3" />
                <div className="text-lg font-medium text-secondary-900 mb-2">
                  No results found
                </div>
                <p className="text-secondary-600 text-center max-w-md">
                  Try different keywords or check the spelling. You can search for tickets, activities, or settings.
                </p>
              </div>
            ) : null}
          </div>

          {/* Footer */}
          <div className="px-6 py-3 bg-secondary-50 border-t border-secondary-200 text-xs text-secondary-500">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <span>Press <kbd className="px-1 py-0.5 bg-white border rounded">↑</kbd> <kbd className="px-1 py-0.5 bg-white border rounded">↓</kbd> to navigate</span>
                <span>Press <kbd className="px-1 py-0.5 bg-white border rounded">Enter</kbd> to select</span>
              </div>
              <span>Press <kbd className="px-1 py-0.5 bg-white border rounded">Esc</kbd> to close</span>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}

export default GlobalSearchModal